import { useCallback, useMemo, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import { selectModelTypeFieldTypeInfo } from 'modules/Models/components/ModelForm/utils/utils';
import { FieldValueMapType, FormikInitialValues } from 'modules/Models/components/ModelForm/utils/types';
import { Button } from 'components/Button';
import { IconAlert, IconCheckCircleV2, IconCheckV2, IconCloseSquare } from 'components/Icons';
import { useModalActions } from 'components/ModalManager/Context';
import { noop } from 'utils/fp';
import { InputField } from 'components/Formik/InputField';
import { FormLabel } from '../FormLabel';
import { modalStyled, fieldContainerStyled, apiModalContainerStyled, apiModalInformationStyled, apiModalFieldContainerStyled, apiModalActionsContainerStyled } from './ApiFormModal.styled';
const API_MODAL_ID = 'api_form_modal_id';
type ApiFormModalProps = React.InputHTMLAttributes<HTMLInputElement> & {
  path: string;
};
export const ApiFormModal = ({
  path,
  disabled
}: ApiFormModalProps) => {
  const {
    openModal,
    closeModal
  } = useModalActions();
  const {
    values,
    setFieldValue
  } = useFormikContext<{
    fieldValueMap: FieldValueMapType;
  }>();
  const {
    fieldValueMap
  } = values;
  const {
    description = ''
  } = selectModelTypeFieldTypeInfo(path, (values as FormikInitialValues));
  const apiKeyValue = useMemo(() => {
    return fieldValueMap['output_info.params.api_key'] ? (fieldValueMap['output_info.params.api_key'] as string) : '';
  }, [fieldValueMap]);
  const updateApiKeyValue = useCallback((value: string) => {
    setFieldValue('fieldValueMap', {
      ...fieldValueMap,
      [path]: value
    });
  }, [fieldValueMap, path]);
  const openApiModal = useCallback(() => {
    openModal({
      className: modalStyled,
      content: <ApiModal defaultValue={apiKeyValue} onChange={updateApiKeyValue} onClose={() => closeModal({
        id: API_MODAL_ID
      })} />,
      id: API_MODAL_ID,
      makeActions: noop,
      title: ''
    });
  }, [fieldValueMap, path]);
  return <div className={fieldContainerStyled}>
      <FormLabel path={path} description={description} />
      <div className="button-container">
        <Button className={`button ${apiKeyValue ? 'api-key-has-value' : ''}`} disabled={disabled} onClick={openApiModal} size="md" variant="secondary">
          {apiKeyValue ? <IconCheckCircleV2 size={15} color="#039855" /> : <IconAlert size={15} />}
          {truncateString(apiKeyValue) || 'Enter API Key'}
        </Button>
        <p className="button-label">
          Reduce your cost by entering your API Key. Requests sent with Clarifai’s API key have additional costs included.{' '}
          <a rel="noreferrer" target="_blank" href="https://docs.clarifai.com/api-guide/predict/text/?__hstc=26175983.3ca05960d3ebedcfd53c0889fa6978f8.1692796697719.1703254625772.1704233402501.37&__hssc=26175983.2.1704233402501&__hsfp=1354570303#use-third-party-api-keys">
            Learn more
          </a>
        </p>
      </div>
    </div>;
};
const ApiModal = ({
  defaultValue,
  onChange,
  onClose
}: {
  defaultValue: string;
  onChange: (value: string) => void;
  onClose: () => void;
}) => {
  const [apiKeyValue, setApiKeyValue] = useState(defaultValue);
  const onInputChange = (change: React.ChangeEvent<HTMLInputElement>) => {
    const value = change?.target.value;
    setApiKeyValue(value);
  };
  const onSave = useCallback(() => {
    onChange(apiKeyValue);
    onClose();
  }, [onChange, apiKeyValue]);
  const onCancel = useCallback(() => {
    onClose();
  }, [onChange, apiKeyValue]);
  return <Formik onSubmit={() => {}} initialValues={{
    apikey: 'inputia'
  }}>
      <div className={apiModalContainerStyled}>
        <div className={apiModalInformationStyled}>
          <h1 className="title">Enter your API Key</h1>

          <p className="description">
            Use the following form to generate a new to represent you in the APIs for all your apps and any apps you’ve been invited to access.{' '}
            <a className="link" href="https://docs.clarifai.com/clarifai-basics/authentication/personal-access-tokens" rel="noreferrer" target="_blank">
              Read Guide
            </a>
          </p>

          <Button className="close-button" variant="tertiary" onClick={onClose}>
            <IconCloseSquare size={20} color="#000000" />
          </Button>
        </div>

        <div className={apiModalFieldContainerStyled}>
          <h4 className="input-label">API Key*</h4>
          <InputField id="path" name="apikey" onChange={onInputChange} placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" type="text" value={apiKeyValue} />
          <p className="input-help-text">Your API Key is stored locally on your browser an never sent anywhere else.</p>
        </div>

        <div className={apiModalActionsContainerStyled}>
          <Button className="action-button" size="md" variant="tertiary" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="action-button save-button" size="md" variant="primary" onClick={onSave}>
            <IconCheckV2 size={18} color="#ffffff" />
            Save
          </Button>
        </div>
      </div>
    </Formik>;
};
const truncateString = (str: string, maxLength = 14) => {
  if (str.length <= maxLength) {
    return str;
  }
  const ellipsis = '...';
  const truncatedLength = maxLength - ellipsis.length;
  const leftLength = Math.ceil(truncatedLength / 2);
  const rightLength = Math.floor(truncatedLength / 2);
  const leftSubstring = str.slice(0, leftLength);
  const rightSubstring = str.slice(str.length - rightLength);
  return leftSubstring + ellipsis + rightSubstring;
};